"use client";

import { Sparkles } from "lucide-react";
import type { CSSProperties } from "react";

import { useCcLatchInView } from "@/lib/use-cc-latch-in-view";
import type { SeoWorkflowInsightsCardCopy } from "./seo-workflow-copy";
import { CcSeoWorkflowCard } from "./CcSeoWorkflowCard";

function SparkleIcon() {
  return (
    <svg
      className="cc-seo-workflow-insights__sparkle"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 1.25L9.35 5.65L13.75 7L9.35 8.35L8 12.75L6.65 8.35L2.25 7L6.65 5.65L8 1.25Z"
        fill="currentColor"
      />
      <path
        d="M13.25 0.75L13.75 2.25L15.25 2.75L13.75 3.25L13.25 4.75L12.75 3.25L11.25 2.75L12.75 2.25L13.25 0.75Z"
        fill="currentColor"
        opacity="0.55"
      />
    </svg>
  );
}

function CcSeoWorkflowInsightsVisual({ copy }: { copy: SeoWorkflowInsightsCardCopy }) {
  const { ref: panelRef, isInView } = useCcLatchInView<HTMLDivElement>({
    threshold: 0.35,
  });

  return (
    <div className="cc-seo-workflow-insights">
      <div
        ref={panelRef}
        className={[
          "cc-seo-workflow-insights__panel",
          isInView && "cc-seo-workflow-insights__panel--in-view",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="cc-seo-workflow-insights__header">
          <div className="cc-seo-workflow-insights__heading">
            <SparkleIcon />
            <span className="cc-seo-workflow-insights__title">{copy.panelTitle}</span>
          </div>
          <span className="cc-seo-workflow-insights__pill">{copy.pill}</span>
        </div>

        <ul className="cc-seo-workflow-insights__list">
          {copy.items.map((insight, index) => (
            <li
              key={insight.text}
              className={`cc-seo-workflow-insights__item cc-seo-workflow-insights__item--${insight.severity}`}
              style={{ "--insights-item-index": index } as CSSProperties}
            >
              <span
                className={`cc-seo-workflow-insights__badge cc-seo-workflow-insights__badge--${insight.severity}`}
              >
                {insight.label}
              </span>
              <span className="cc-seo-workflow-insights__text">{insight.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

type CcSeoWorkflowInsightsProps = {
  copy: SeoWorkflowInsightsCardCopy;
};

export function CcSeoWorkflowInsights({ copy }: CcSeoWorkflowInsightsProps) {
  return (
    <CcSeoWorkflowCard
      id="insights"
      title={copy.title}
      description={copy.description}
      badge={copy.badge}
      icon={Sparkles}
    >
      <CcSeoWorkflowInsightsVisual copy={copy} />
    </CcSeoWorkflowCard>
  );
}
